import React from "react";
import { FormattedMessage } from "react-intl";
import { formatLink } from "../../utils/functions";
import { AiOutlineCheck } from "react-icons/ai";

interface FormCheckBoxProps {
  id: string;
  options?: string[];
  question: string;
  repeatable?: boolean;
  divider?: string;
  inputStyle?: string;
  labelStyle?: string;
  divStyle?: string;
  dividerHeaderStyle?: string;
  optionLabelStyle?: string;
  containerStyle?: string;
  register?: any;
  errors?: Record<string, Object>;
  link?: string | boolean;
  cursiveLabel?: string;
  repeatableHeader?: string;
  repeatableKey?: { key: string; last: boolean; amount: number };
  checked: string[];
  index?: number;
  centerCheckBox?: boolean;
  validate?: (id: string) => void;
  changeAmount?: (key: string, direction: number) => void;
  handleInputChange: (
    event: React.ChangeEvent<HTMLElement>,
    id: string,
    type?: string
  ) => void;
}

export const FormCheckBox: React.FC<FormCheckBoxProps> = ({
  id,
  options,
  handleInputChange,
  question,
  divider,
  inputStyle,
  labelStyle,
  divStyle,
  cursiveLabel,
  checked,
  repeatableKey,
  index,
  centerCheckBox,
  containerStyle,
  dividerHeaderStyle,
  repeatable,
  optionLabelStyle,
  register,
  errors,
  link,
  repeatableHeader,
  changeAmount,
  validate,
}) => {
  const styles = inputStyle
    ? inputStyle
    : "text-gray-700 transition transform duration-500 ease-in-out hover:scale-105";

  const divStyles = divStyle
    ? divStyle
    : "flex flex-col mb-4 xl:w-11/12 xl:m-auto xl:pt-2";

  const labelStyles = labelStyle
    ? labelStyle
    : "mb-2 ml-2 uppercase font-bold text-lg text-brand-dark_blue";

  const linkStyles = "hover:text-brand-dark_blue text-brand-blue xl:mb-2";

  const participantId = repeatable ? id.split("_")[1] : ""; //expected id is 'BLABLABLA_2' => 2

  const dividerHeaderStyles = dividerHeaderStyle
    ? dividerHeaderStyle
    : "m-2 uppercase font-bold text-lg text-brand-blue";

  const optionLabelStyles = optionLabelStyle
    ? optionLabelStyle
    : "text-lg text-brand-dark_blue relative cursor-pointer select-none";

  const dividerStyles = divider ? divider : "";
  const containerStyles = containerStyle ? containerStyle : "";

  const requiredAsterisk =
    register === null || register === undefined ? null : (
      <span className='font-normal text-red-500'>*</span>
    );

  const errorDiv = errors ? (
    errors[id] ? (
      <p className='text-red-400 ml-4'>
        {" "}
        <FormattedMessage id={errors[id]["message"]} />
      </p>
    ) : null
  ) : null;

  const labelTag =
    typeof link === "string" ? (
      <span className={`${linkStyles} xl:mt-3 mt-6`}>
        <a href={formatLink(link)} target='_blank' rel='noopener noreferrer'>
          {question}
          {requiredAsterisk}
        </a>
      </span>
    ) : (
      <span className={`${labelStyles}`}>
        {question}
        {requiredAsterisk}
      </span>
    );

  const additionalLabel = cursiveLabel ? (
    <p className='text-brand-dark_blue italic ml-4'>{cursiveLabel}</p>
  ) : null;

  const optionsDiv = "xl:flex xl:flex-wrap xl:pb-5 xl:pt-2 pl-8 xl:pl-6";

  const buttonStyle =
    "bg-blue-400 hover:bg-brand-blue text-brand-white py-2 px-4 rounded m-5 w-auto";
  const lastOfRepeatGroup = repeatableKey
    ? repeatableKey.last
      ? true
      : false
    : false;

  const isChecked = (option: string, checked: string[]) => {
    return (
      checked.find((checkedOption) => checkedOption === option) !== undefined
    );
  };

  const repeatableButtons = lastOfRepeatGroup ? (
    <div className='flex flex-row justify-between'>
      <button
        onClick={() => changeAmount(repeatableKey.key, 1)}
        type='button'
        className={`${buttonStyle}`}
      >
        <FormattedMessage id='Form.Repeat_add' />{" "}
        {repeatableHeader.toLocaleLowerCase()}
      </button>
      <button
        onClick={() => changeAmount(repeatableKey.key, -1)}
        type='button'
        className={`${buttonStyle}`}
      >
        <FormattedMessage id='Form.Repeat_remove' />{" "}
        {repeatableHeader.toLocaleLowerCase()}
      </button>
    </div>
  ) : null;

  return (
    <div className={`${divStyles} ${dividerStyles} ${containerStyles}`}>
      {divider && repeatable ? (
        <span className={`${dividerHeaderStyles}`}>
          {repeatableHeader} {participantId}
        </span>
      ) : null}
      {labelTag}
      {additionalLabel}
      <fieldset tabIndex={index}>
        <div className={optionsDiv}>
          {options?.map((option, i) => (
            <div
              key={option}
              className={`w-full ${
                centerCheckBox ? "" : "xl:w-1/3"
              } pl-4 pt-3 xl:pt-0 ${styles}`}
            >
              <label
                htmlFor={`${id}:${option}`}
                className={`${optionLabelStyles} multiSelect`}
                tabIndex={i}
              >
                <input
                  name={id}
                  value={option || ""}
                  type='checkbox'
                  id={`${id}:${option}`}
                  ref={register}
                  onChange={(e) => handleInputChange(e, id, "array")}
                  checked={isChecked(option, checked)}
                  onBlur={validate ? () => validate(id) : null}
                />
                <span
                  className={`absolute top-0 -left-6 h-4 w-4 ${
                    isChecked(option, checked)
                      ? "bg-blue-400"
                      : "bg-blue-100 checkmark"
                  }`}
                >
                  {isChecked(option, checked) ? (
                    <AiOutlineCheck
                      style={{
                        paddingBottom: "3px",
                        paddingRight: "3px",
                        color: "#00255f",
                      }}
                    />
                  ) : null}
                </span>
                {option}
              </label>
            </div>
          ))}
        </div>
      </fieldset>
      {errorDiv}
      {repeatableButtons}
    </div>
  );
};

export default FormCheckBox;
